/** Reset scroll position for full-bleed Apple TV routes (window + common containers). */
export { isAppleDetailPath } from "./appleTvHost";

export function scrollAppleSurfaceToTop() {
  if (typeof window === "undefined") return;
  window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0;
  const root = document.getElementById("root");
  const app = document.getElementById("app");
  if (root) root.scrollTop = 0;
  if (app) app.scrollTop = 0;
}

/** True page offset — max across the usual scroll roots. */
export function readApplePageScrollY() {
  if (typeof window === "undefined") return 0;
  const se = document.scrollingElement;
  return Math.max(
    Number(window.scrollY) || 0,
    Number(window.pageYOffset) || 0,
    Number(document.documentElement?.scrollTop) || 0,
    Number(document.body?.scrollTop) || 0,
    Number(se?.scrollTop) || 0,
  );
}

function isApplePageScrollTarget(target) {
  if (!target || target === window || target === document) return true;
  if (target === document.documentElement || target === document.body) return true;
  if (target === document.scrollingElement) return true;
  return false;
}

/**
 * Keep top-bar scrolled chrome stable: shelf/carousel scroll events must not
 * flip the bar back to the solid “at top” treatment while the page is mid-scroll.
 */
export function bindAppleTopBarScrollState(setScrolled, options = {}) {
  if (typeof window === "undefined") return () => {};
  const scrollOn = Number(options.scrollOn) > 0 ? Number(options.scrollOn) : 16;
  const scrollOff = Number(options.scrollOff) >= 0 ? Number(options.scrollOff) : 2;
  let locked = readApplePageScrollY() > scrollOn;

  const apply = (next) => {
    locked = next;
    setScrolled((prev) => (prev === next ? prev : next));
  };

  const sync = (event) => {
    const y = readApplePageScrollY();
    const pageScroll = isApplePageScrollTarget(event?.target);

    // Nested horizontal shelves / hero carousels fire capture-phase scroll too.
    // Never let those force the solid dark “at top” bar while mid-page.
    if (!pageScroll) {
      if (y > scrollOn) apply(true);
      return;
    }

    apply(locked ? y > scrollOff : y > scrollOn);
  };

  sync();
  window.addEventListener("scroll", sync, { passive: true, capture: true });
  window.addEventListener("resize", sync, { passive: true });
  const vv = window.visualViewport;
  vv?.addEventListener("resize", sync);
  vv?.addEventListener("scroll", sync);
  return () => {
    window.removeEventListener("scroll", sync, { capture: true });
    window.removeEventListener("resize", sync);
    vv?.removeEventListener("resize", sync);
    vv?.removeEventListener("scroll", sync);
  };
}

/** Reset one horizontal shelf so the first card aligns with the section title. */
export function resetHorizontalTrack(el) {
  if (!el) return;
  el.scrollLeft = 0;
  if (typeof el.scrollTo === "function") {
    el.scrollTo({ left: 0, behavior: "auto" });
  }
}

/** Reset horizontal shelf tracks so the first card is aligned on load. */
export function resetAppleShelfTracks(container) {
  if (!container) return;
  container.querySelectorAll(".apple-clone__track:not(.apple-clone__track--wrap)").forEach(resetHorizontalTrack);
}
